/**
 * Policy Global Page — tenant-wide policy layer + effective preview per runner.
 */

import { useState } from 'react';
import { Button, Card, Spinner } from '@/components/ui';
import { ShieldCheck } from 'lucide-react';
import { PageHeader } from '@vanguard-atlas/shared';
import { useI18n } from '@/i18n';
import { useRunners } from '@/features/mcp';
import {
  useGlobalPolicy,
  useRunnerEffectivePolicy,
  useSaveGlobalPolicy,
} from '@/features/mcp/hooks/usePolicyLayers';
import type { RunnerPolicyConfig } from '@/features/mcp/hooks/usePolicyLayers';
import { RunnerPolicyForm } from './components/RunnerPolicyForm';

function EffectivePanel({ runnerId }: { runnerId: string | null }) {
  const { data, isLoading } = useRunnerEffectivePolicy(runnerId);
  if (!runnerId) return null;
  if (isLoading) {
    return (
      <div className="flex justify-center p-4">
        <Spinner />
      </div>
    );
  }
  if (!data) return null;
  const cfg = data.config;
  return (
    <Card className="space-y-2 p-4">
      <h3 className="font-semibold">Política efectiva</h3>
      <dl className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <dt className="text-muted-foreground">Modo</dt>
          <dd>{cfg.mode ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Perfil</dt>
          <dd>{data.profile_name ?? 'global'}</dd>
        </div>
        <div className="col-span-2">
          <dt className="text-muted-foreground">Aprobación (keywords)</dt>
          <dd className="font-mono text-xs">{cfg.approval_required_for?.join(', ') ?? '—'}</dd>
        </div>
        <div className="col-span-2">
          <dt className="text-muted-foreground">Directorios permitidos</dt>
          <dd className="font-mono text-xs">{cfg.allowed_paths?.join(', ') ?? '—'}</dd>
        </div>
        <div className="col-span-2">
          <dt className="text-muted-foreground">Directorios bloqueados</dt>
          <dd className="font-mono text-xs">{cfg.blocked_paths?.join(', ') ?? '—'}</dd>
        </div>
      </dl>
    </Card>
  );
}

export function PolicyGlobalPage() {
  const { t } = useI18n();
  const { data: globalConfig, isLoading } = useGlobalPolicy();
  const { data: runnerList } = useRunners();
  const saveGlobal = useSaveGlobalPolicy();
  const [draft, setDraft] = useState<RunnerPolicyConfig | null>(null);
  const [selectedRunner, setSelectedRunner] = useState<string | null>(null);

  const value = draft ?? globalConfig ?? {};

  if (isLoading) {
    return (
      <div className="flex justify-center p-6">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        icon={ShieldCheck}
        title={t('admin.integrations.globalPolicy') ?? 'Política global de runners'}
        subtitle="Capa de menor prioridad — aplica a todos los runners del tenant salvo override de perfil o runner."
        testId="runners-policy-header"
      />

      <RunnerPolicyForm value={value} onChange={setDraft} layer="global" />

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => setDraft(null)} disabled={draft === null}>
          Descartar
        </Button>
        <Button
          onClick={async () => {
            if (draft !== null) {
              await saveGlobal.mutateAsync(draft);
              setDraft(null);
            }
          }}
          disabled={draft === null || saveGlobal.isPending}
        >
          {saveGlobal.isPending ? 'Guardando…' : 'Guardar política global'}
        </Button>
      </div>

      <Card className="space-y-3 p-4">
        <h3 className="font-semibold">Vista previa por runner</h3>
        <select
          value={selectedRunner ?? ''}
          onChange={(e) => setSelectedRunner(e.target.value || null)}
          className="w-full max-w-md rounded border border-border bg-background px-2 py-1.5 text-sm"
        >
          <option value="">Selecciona un runner…</option>
          {(runnerList?.runners ?? []).map((runner) => (
            <option key={runner.agent_id} value={runner.agent_id}>
              {runner.name} ({runner.status})
            </option>
          ))}
        </select>
        <EffectivePanel runnerId={selectedRunner} />
      </Card>
    </div>
  );
}
